import type { Lang, LorsCopy } from "./translations";

export type Sep23Headline = { headline: string; subhead: string };

export const sep23Headlines: Sep23Headline[] = [
  {
    headline: "Your Home Deserves a Better Plan Than Probate Court",
    subhead:
      "A home is more than an asset — it's your family's foundation. Without the right legal plan, a California court can decide what happens to it. In this free September 23rd webinar, attorney Ruby Steinbrecher shows you how to keep your home out of probate and in your family's hands.",
  },
  {
    headline: "Keep Your Home in the Family — Without the Courthouse",
    subhead:
      "Probate can tie up your home for months or years, draining value with fees and delays. The good news: it's avoidable. Join our free live webinar on September 23rd to learn the simple legal steps California homeowners use to pass property directly to their loved ones.",
  },
  {
    headline: "The Difference Between a Will and a Trust Could Cost Your Family Thousands",
    subhead:
      "A will names your wishes, but it doesn't keep your family out of probate court. A properly funded trust can. In this free September 23rd webinar, we'll break down what each document actually does — and which one your family needs.",
  },
  {
    headline: "One Protects Your Family. The Other Sends Them to Court.",
    subhead:
      "Many people believe a will is enough — until their family ends up in probate. In this free webinar on September 23rd, attorney Ruby Steinbrecher explains the one document that can keep your home, your savings, and your legacy out of the court system.",
  },
  {
    headline: "Don't Let Your Family Learn About Probate the Hard Way",
    subhead:
      "Probate is often discovered after it's too late to avoid. This free September 23rd webinar gives California families a clear, practical overview of how probate works — and the planning steps that can spare your loved ones from the process entirely.",
  },
  {
    headline: "You Already Own Enough to Need a Trust. Here's What to Do Next.",
    subhead:
      "If you own a home in California, your estate is likely above the probate threshold. That means a trust isn't just useful — it's essential. Join our free webinar on September 23rd to learn what kind of trust fits your situation and how to fund it properly.",
  },
  {
    headline: "A Paid-Off Mortgage Doesn't Mean Your Home Is Protected",
    subhead:
      "Owning your home free and clear is a huge accomplishment — but it doesn't shield it from probate court. In this free September 23rd webinar, we'll show you how to title and structure your home so it passes to your family without court interference.",
  },
  {
    headline: "The Gaps in Your Plan You Don't Know About Yet",
    subhead:
      "Even families with good intentions often have estate plans with missing pieces — unfunded trusts, outdated deeds, or documents that don't work together. In this free webinar on September 23rd, we'll help you spot the gaps before they become problems.",
  },
  {
    headline: "Your Plan Might Be Outdated. Here's How to Tell.",
    subhead:
      "Laws change. Families change. And an estate plan that made sense five years ago may no longer protect your home or your heirs. Join attorney Ruby Steinbrecher on September 23rd for a free webinar on how to review and update your plan.",
  },
  {
    headline: '"Someday" Is How Families End Up in Probate Court',
    subhead:
      "Estate planning is easy to postpone — but the cost of waiting often falls on the people you love most. This free September 23rd webinar will help you take the first step now, with clear guidance and no pressure.",
  },
  {
    headline: "One Document Stands Between Your Family and Probate Court",
    subhead:
      "For most California homeowners, a properly funded revocable living trust is the document that makes the difference. In this free webinar on September 23rd, we'll explain what it is, how it works, and how to set one up the right way.",
  },
  {
    headline: "The Number That Decides Whether Your Family Goes to Court",
    subhead:
      "California's probate threshold is $184,500. If the value of your assets exceeds that, your estate may be headed for court — unless you plan ahead. Learn what that number means for your family in our free September 23rd webinar.",
  },
];

const baseEn = {
  eyebrow: "Free Estate Planning Webinar",
  headline: sep23Headlines[0].headline,
  headlineBreakBefore: "Learn How to Avoid It",
  subhead: sep23Headlines[0].subhead,
  subheadLabel: "A note from Ruby:",
  learnTitle: "What Most California Homeowners Don't Realize Until It's Too Late",
  learnItems: [
    "Why a will alone won't keep your home out of California probate court",
    "How a properly funded living trust passes your home directly to your family — no court, no delays",
    "The 2025 California probate law changes and what they mean for Bay Area homeowners",
    "California probate fees on a $700,000 estate can exceed $46,000. A solid estate plan? A fraction of that — and your family gets everything, faster.",
  ],
  quote:
    "Ruby Steinbrecher was fantastic. The family trust workshop was clear, informative, and extremely helpful for getting started with estate planning.",
  quoteAttribution: "Shawn H., Workshop Attendee",
  formDate: { short: "Wed, September 23rd", long: "Wednesday, September 23rd" },
  formTime: "12:00 PM PT",
  formVenueLine1: "Live Online Webinar",
  formVenueLine2: "Join from anywhere — link sent after registration",
  formTitle: "Reserve Your Seat",
  formSubtitle: "Free to attend. Limited spots available.",
  formFirstName: "First name",
  formLastName: "Last name",
  formEmail: "Email",
  formPhone: "Phone",
  formSmsConsent:
    "I consent to receive text messages from the Law Office of Ruby Steinbrecher about this event, and occasional marketing messages. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.",
  formEnewsConsent:
    "I'd like to receive occasional email updates from the Law Office of Ruby Steinbrecher.",
  formPrivacy: "Privacy Policy",
  formSubmit: "Reserve My Seat",
  formSubmitting: "Reserving your seat…",
  formTrust: "⭐ Trusted by local families | 81 Five-Star Reviews",
  getDirections: "",
  footerCopyright: "© 2026 Ruby Steinbrecher",
  footerPrivacy: "Privacy Policy",
  testimonialsTitle: "What Families Are Saying",
  aboutTitle: "Meet Ruby Steinbrecher",
  aboutRole: "Attorney",
  aboutBody: [
    "I have practiced law since 2007, first working at a medium-sized firm in San Francisco handling civil litigation, and then moving into estate planning, small business and contract law since 2010. I value people, the planet, and prosperity that does not adversely affect the former.",
    "Prior to entering the legal field, I had a variety of business and life experience that has only enhanced my ability to serve clients: Road Manager for Ken Kesey's bus trip to Great Britain in 1999, Human Resources at a tech startup in 2001, recognized for my work as a Case Manager at Casey Family Programs with disadvantaged youth in the foster care system in Oakland and the wider San Francisco-Bay Area in the early 2000's.",
    "Currently, I live in Sebastopol with my daughter, husband, and a menagerie of pets, where I enjoy gardening as it keeps me grounded and enjoying the beautiful weather.",
  ],
  registrationTitle: "Ready to Secure Your Family's Future?",
  registrationSub: "Free to attend. Limited spots available.",
  registrationCta: "Save My Seat",
  registrationFoot:
    "No pressure. Just clear information so you can make the right decision for your family.",
  errorTitle: "Something went wrong",
  errorBody:
    "We couldn't reserve your seat. Please try again or call our office.",
  validation: {
    firstName: "First name is required",
    lastName: "Last name is required",
    email: "Enter a valid email",
    phone: "Enter a valid phone number",
  },
  timeAgo: {
    monthAgo: "a month ago",
    monthsAgo: (n: number) => `${n} months ago`,
    yearAgo: "a year ago",
  },
};

export const lorsContentSep23 = { en: baseEn, es: baseEn } as unknown as Record<Lang, LorsCopy>;

export const getSep23Copy = (hParam: string | null): LorsCopy => {
  const n = Number(hParam);
  const idx = Number.isInteger(n) && n >= 1 && n <= sep23Headlines.length ? n - 1 : 0;
  const pick = sep23Headlines[idx];
  return { ...baseEn, headline: pick.headline, subhead: pick.subhead } as unknown as LorsCopy;
};
